import { ArrowLeft } from "lucide-react";

import type { PremioPublic, SoundStyle } from "@juegossc/shared";
import { Wheel } from "./Wheel";
import { Flapper } from "./Flapper";
import { SpinButton } from "./SpinButton";
import { SCREEN_W, SCREEN_H } from "../landing/FitScreen";

// Markup de la PANTALLA de la ruleta sobre el lienzo de referencia 1080x1920.
// Misma idea que LandingView: fuente de verdad UNICA compartida por la pantalla
// EN VIVO (routes/Ruleta.tsx, via FitScreen) y el PREVIEW del admin
// (JuegoPreview, via VirtualScreen). Como los dos escalan ESTE mismo markup
// sobre el mismo lienzo, el preview coincide 100% con la pantalla real a
// cualquier resolucion: si en la pantalla hay X% de margen al costado de la
// rueda, el preview muestra ese mismo X% (pedido del dueño). Transparente: el
// fondo lo pone FitScreen / VirtualScreen.
//
// Solo presentacional. El estado del giro (spinning, target, datos, modales)
// vive en routes/Ruleta.tsx; aca recibimos props para dibujar. En `preview` los
// controles no navegan ni giran.

// Tamaño de la rueda sobre el lienzo (no sobre window) -> misma proporcion en
// preview y en vivo. Migration 044: el TARGET es configurable (deslizador en
// Ajustes › Juegos › Ruleta); RuletaView lo clampea al espacio disponible del
// lienzo (min con 1080-32 y 1920-200) para que la rueda nunca se recorte.
// Default 520 = el cap histórico hardcodeado.
const WHEEL_SIZE_DEFAULT = 520;

export function RuletaView({
  campana,
  campanaHeightPx,
  campanaTopMarginPx,
  premios,
  colorBorde,
  colorCentro,
  grosorDivisoria,
  textAlign,
  verticalPosPct = 50,
  soundStyle,
  physics,
  spinning,
  targetIndex,
  onSpinEnd,
  onTick,
  flapperTick = 0,
  flapperIntensity = 0,
  onSpin,
  spinDisabled = false,
  playError,
  preview = false,
  onBack,
  dprMax = 2,
  wheelSize = WHEEL_SIZE_DEFAULT,
}: {
  campana: { nombre_display: string; imagen_url: string } | null;
  campanaHeightPx: number;
  campanaTopMarginPx: number;
  premios: PremioPublic[];
  colorBorde: string;
  colorCentro: string;
  grosorDivisoria: number;
  textAlign: "left" | "center" | "right";
  /** Posición vertical del cuerpo del juego (la rueda), 0..100 (migration 037).
   *  0 = pegado a la campaña, 50 = centrado (default), 100 = abajo. Se aplica
   *  con espaciadores flex que reparten el espacio libre -> nunca se recorta. */
  verticalPosPct?: number;
  soundStyle?: SoundStyle;
  physics?: {
    velocidadMin: number;
    variacion: number;
    frenado: number;
    umbralCasiPasa: number;
  };
  spinning: boolean;
  targetIndex: number;
  onSpinEnd?: () => void;
  onTick?: (intensity: number) => void;
  flapperTick?: number;
  flapperIntensity?: number;
  onSpin?: () => void;
  spinDisabled?: boolean;
  playError?: string | null;
  preview?: boolean;
  onBack?: () => void;
  /** Migration 040: cap de DPR del canvas de la rueda (nitidez). Default 2. */
  dprMax?: number;
  /** Migration 044: tamaño TARGET de la rueda en px (se clampea al lienzo). */
  wheelSize?: number;
}) {
  // Clamp al espacio del lienzo: la rueda nunca se recorta aunque el target sea
  // mayor que lo que entra.
  const wheelPx = Math.min(SCREEN_W - 32, SCREEN_H - 200, wheelSize);
  // Migration 037: posición vertical configurable (0..100) via espaciadores
  // flex. La rueda (shrink-0) mantiene su tamaño; el espacio libre se reparte
  // arriba/abajo según el % -> la rueda NUNCA se recorta.
  const topGrow = Math.max(0, Math.min(100, verticalPosPct));
  const bottomGrow = 100 - topGrow;
  return (
    <div className="app-mode flex h-full w-full flex-col">
      <header className="flex items-center justify-between gap-4 p-4">
        <button
          type="button"
          onClick={preview ? undefined : onBack}
          disabled={preview}
          tabIndex={preview ? -1 : 0}
          className="flex shrink-0 items-center gap-2 rounded-lg bg-white/10 px-4 py-2 hover:bg-white/20"
        >
          <ArrowLeft className="h-5 w-5" />
          Atrás
        </button>
        <div className="flex min-w-0 flex-1 items-center justify-center">
          {campana?.imagen_url ? (
            <img
              src={campana.imagen_url}
              alt={campana.nombre_display}
              style={{
                height: `${campanaHeightPx}px`,
                marginTop: `${campanaTopMarginPx}px`,
              }}
              className="w-auto max-w-full shrink-0 object-contain drop-shadow-lg"
            />
          ) : (
            <h1 className="font-heading text-xl font-bold opacity-80">RULETA</h1>
          )}
        </div>
        <div className="w-20 shrink-0" />
      </header>

      <section className="flex flex-1 flex-col items-center p-4">
        {/* Espaciador superior: crece según el % de posición. */}
        <div aria-hidden style={{ flex: `${topGrow} 1 0` }} />

        <div
          className="relative shrink-0"
          style={{ width: wheelPx, height: wheelPx }}
        >
          <Wheel
            premios={premios}
            size={wheelPx}
            targetIndex={targetIndex}
            spinning={spinning}
            onSpinEnd={onSpinEnd}
            physics={physics}
            onTick={onTick}
            colorBorde={colorBorde}
            grosorDivisoria={grosorDivisoria}
            textAlign={textAlign}
            soundStyle={soundStyle}
            dprMax={dprMax}
          />
          <Flapper
            size={36}
            bumpTick={flapperTick}
            bumpIntensity={flapperIntensity}
            spinning={spinning}
            color={colorCentro}
          />
          <SpinButton
            onClick={preview ? () => {} : onSpin ?? (() => {})}
            disabled={preview || spinDisabled}
            color={colorCentro}
          />
        </div>

        {playError && (
          <div className="mt-6 shrink-0 rounded-lg bg-red-500/15 px-4 py-2 text-sm text-red-300">
            {playError}
          </div>
        )}

        {/* Espaciador inferior: el resto del espacio libre. */}
        <div aria-hidden style={{ flex: `${bottomGrow} 1 0` }} />
      </section>
    </div>
  );
}
